import React from 'react';
import isPropValid from '@emotion/is-prop-valid';
import type { DialogTitleProps, DialogDescriptionProps } from '@radix-ui/react-dialog';
import styled, { keyframes } from 'styled-components';
import classNames from 'classnames';
import * as RadixDialog from '@radix-ui/react-dialog';
import { deviceSizes } from '../../style';

// components
import { BoxProps, Section } from '../box';
import Icon from '../icon';
import { SquareButton } from '../button';

// styles
import { ButtonProps } from '../button/button';
import { styleModes, widths } from './style';
import type { ModalProps } from './modal';
import { StyledOverlay } from './modal';

interface SideModalBodyProps {
  width: keyof typeof widths;
  overflowY: ModalProps['overflowY'];
  styleMode?: keyof typeof styleModes;
  'aria-labelledby'?: string;
  children?: React.ReactNode;
  className?: string;
}

const slideAnimation = keyframes`
  0% {
    right: -4000px
  }

  100% {
    right: 0
  }

`;

const SideModalContentBase = (props: SideModalBodyProps & React.HTMLAttributes<HTMLDivElement>) => (
  <RadixDialog.Content aria-describedby={props['aria-describedby']} {...props} />
);

const StyledSideModalBody = styled(SideModalContentBase).withConfig({
  shouldForwardProp: prop => !['overflowY', 'width', 'styleMode'].includes(prop) && isPropValid(prop),
})<SideModalBodyProps>`
  width: ${props => widths[props.width]};
  height: 100%;
  top: 0;
  right: 0;
  bottom: 0;
  background: white;
  position: fixed;
  z-index: 1300;
  ${props => props.styleMode && styleModes[props.styleMode]};

  @media (max-width: ${deviceSizes.sm}) {
    width: 100% !important;
  }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${slideAnimation} 0.5s;
  }
`;

const SideModal: React.FunctionComponent<ModalProps> & {
  ScrollableContainer: React.FunctionComponent<BoxProps>;
  CloseButton: React.FunctionComponent<ButtonProps>;
  Title: React.FunctionComponent<DialogTitleProps>;
  Description: React.FunctionComponent<DialogDescriptionProps>;
} = ({
  width = 'lg',
  className,
  show,
  onHide,
  'data-testid': dataTestId,
  'aria-labelledby': ariaLabelledby,
  'aria-describedby': ariaDescribedby = undefined,
  overflowY,
  styleMode,
  children,
  modal,
  ...props
}: ModalProps) => {
  return (
    <RadixDialog.Root
      open={show}
      onOpenChange={open => {
        if (!open) {
          onHide?.();
        }
      }}
      modal={modal}
    >
      <RadixDialog.Portal>
        <StyledOverlay />
        <StyledSideModalBody
          className={classNames(className, 'box-modal', 'box-side-modal')}
          data-testid={dataTestId}
          data-slot="modal-content"
          width={width}
          overflowY={overflowY}
          styleMode={styleMode}
          aria-labelledby={ariaLabelledby}
          aria-describedby={ariaDescribedby}
          {...props}
        >
          {children}
        </StyledSideModalBody>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
};

SideModal.ScrollableContainer = styled(Section)<BoxProps>`
  height: 100%;
  overflow-y: auto;
`;

SideModal.CloseButton = styled(({ className, ...rest }: ButtonProps) => {
  return (
    <SquareButton
      aria-label={'Close'}
      variant={'secondary-clean'}
      size={'md'}
      className={classNames('side-modal-close-icon', className)}
      {...rest}
    >
      <Icon clickable variant={'inherit'} weight={500} size={'xl'} name={'CLOSE'} />
    </SquareButton>
  );
})<ButtonProps>`
  &.side-modal-close-icon {
    position: ${props => props.position || 'absolute'};
    right: 40px;
    top: 32px;
    z-index: 9999;
    cursor: pointer;

    @media (max-width: ${deviceSizes.sm}) {
      font-size: 2rem;
      right: 8px;
      top: 8px;
    }
  }
`;

SideModal.Title = ({ asChild = true, children, ...rest }: DialogTitleProps) => (
  <RadixDialog.Title asChild={asChild} {...rest}>
    {children}
  </RadixDialog.Title>
);

SideModal.Description = ({ asChild = true, children, ...rest }: DialogTitleProps) => (
  <RadixDialog.Description asChild={asChild} {...rest}>
    {children}
  </RadixDialog.Description>
);

export default SideModal;
